/**
 * E012-T015: CrossDocRelationService
 *
 * Service für Cross-Doc Relation Operations.
 * Ermöglicht das Erstellen und Löschen von bidirektionalen Relations
 * über Doc-Grenzen hinweg mit korrekten Permission-Checks.
 *
 * Verwendung:
 * - Wird von RelationConfigMenu via Bridge aufgerufen
 * - Prüft Doc_Update Permission vor Modification
 * - Öffnet Target-Doc, führt Operation aus, released Doc
 */

import { createDatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { Service } from '@toeverything/infra';

import {
  getDatabaseRowsWindow,
  getRowDisplayValueFromDataSource,
  type RelationRowsWindow,
  type RelationRowsWindowRequest,
} from '../../../blocksuite/database-block/properties/relation/row-projection';
import type { DocsService } from '../../doc';
import type { GuardService } from '../../permissions';

/**
 * E012-T015: Error when user lacks permission to modify target doc
 */
export class PermissionDeniedError extends Error {
  constructor(
    public readonly docId: string,
    message?: string
  ) {
    super(message ?? `No write permission on document: ${docId}`);
    this.name = 'PermissionDeniedError';
  }
}

/**
 * E012-T015: Error when target database is not found
 */
export class DatabaseNotFoundError extends Error {
  constructor(
    public readonly databaseId: string,
    public readonly docId: string
  ) {
    super(`Database ${databaseId} not found in document ${docId}`);
    this.name = 'DatabaseNotFoundError';
  }
}

/**
 * Helper to extract DatabaseBlockModel from a Block.
 *
 * BlockSuite's getBlocksByFlavour returns Block[], but when querying
 * 'affine:database', the block's model is a DatabaseBlockModel.
 * This function provides type-safe access with runtime validation.
 */
function getDatabaseBlockModel(block: {
  model?: unknown;
  flavour?: string;
}): DatabaseBlockModel {
  if (!block.model) {
    throw new Error('Block has no model');
  }
  return block.model as DatabaseBlockModel;
}

/**
 * Result of a cross-doc relation operation
 */
export interface CrossDocRelationResult {
  success: boolean;
  inversePropertyId?: string;
  error?: Error;
}

export class CrossDocRelationService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly guardService: GuardService
  ) {
    super();
  }

  /**
   * E012-T015: Check if user can modify a document
   */
  async canModifyDoc(docId: string): Promise<boolean> {
    try {
      return await this.guardService.can('Doc_Update', docId);
    } catch (e) {
      console.warn(
        `[CrossDocRelation] Permission check failed for ${docId}:`,
        e
      );
      // On error, deny access for safety
      return false;
    }
  }

  /**
   * E012-T015: Create an inverse relation in a target document.
   *
   * This handles Cross-Doc scenarios where the target database
   * is in a different document than the source.
   *
   * @param sourceDocId - Doc ID where the original relation lives
   * @param sourceDatabaseId - Database ID of the source (for backlink target)
   * @param targetDocId - Doc ID where inverse relation should be created
   * @param targetDatabaseId - Database ID where inverse relation should be created
   * @param backlinkName - Name for the inverse relation column
   * @param sourceRelationId - Property ID of the original relation (for inverseFieldId)
   * @param inverseCardinality - Cardinality of the inverse relation
   */
  async createCrossDocInverseRelation(
    sourceDocId: string,
    sourceDatabaseId: string,
    targetDocId: string,
    targetDatabaseId: string,
    backlinkName: string,
    sourceRelationId: string,
    inverseCardinality: 'one' | 'many' = 'many',
    sourceCardinality: 'one' | 'many' = 'many'
  ): Promise<CrossDocRelationResult> {
    // 1. Permission Check
    const canModify = await this.canModifyDoc(targetDocId);
    if (!canModify) {
      const error = new PermissionDeniedError(targetDocId);
      console.warn('[CrossDocRelation] Permission denied:', error.message);
      return { success: false, error };
    }

    // 2. Open Target Doc
    let doc, release;
    try {
      const openResult = this.docsService.open(targetDocId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.error('[CrossDocRelation] Failed to open target doc:', e);
      return {
        success: false,
        error: new Error(`Failed to open document: ${targetDocId}`),
      };
    }

    try {
      // 3. Get BlockSuite doc and find target database
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        throw new Error('BlockSuite doc not available');
      }

      // Ensure doc is fully loaded before querying blocks
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === targetDatabaseId
      );

      if (!targetDb) {
        throw new DatabaseNotFoundError(targetDatabaseId, targetDocId);
      }

      // 4. Find title property ID of source database (for targetFieldId)
      // We need to open source doc to find this
      const sourceTitleFieldId = await this.findTitlePropertyId(
        sourceDocId,
        sourceDatabaseId
      );

      // 5. Create the inverse relation property
      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      // IMPORTANT: Set inverseCardinality to sourceCardinality for symmetric stealing!
      // When editing from the inverse column, we need to check if the SOURCE side
      // has cardinality 'one' to trigger the steal dialog.
      const inversePropertyId = dataSource.propertyAdd('end', {
        type: 'relation',
        name: backlinkName,
      });

      if (!inversePropertyId) {
        throw new Error('Failed to add inverse relation property');
      }

      dataSource.propertyDataSet(inversePropertyId, {
        targetDocId: sourceDocId, // Points back to source doc
        targetDatabaseId: sourceDatabaseId, // Points back to source database
        targetFieldId: sourceTitleFieldId,
        cardinality: inverseCardinality, // Cardinality of THIS column
        inverseFieldId: sourceRelationId, // Links to original relation
        inverseCardinality: sourceCardinality, // Cardinality of the SOURCE column (for symmetric stealing)
      });

      return { success: true, inversePropertyId };
    } catch (e) {
      console.error('[CrossDocRelation] Failed to create inverse relation:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      // 6. Always release the doc
      release();
    }
  }

  /**
   * E012-T016: Delete an inverse relation in a target document.
   * (Prepared for T016 implementation)
   */
  async deleteCrossDocInverseRelation(
    targetDocId: string,
    targetDatabaseId: string,
    inverseFieldId: string
  ): Promise<CrossDocRelationResult> {
    // 1. Permission Check
    const canModify = await this.canModifyDoc(targetDocId);
    if (!canModify) {
      const error = new PermissionDeniedError(targetDocId);
      console.warn('[CrossDocRelation] Permission denied:', error.message);
      return { success: false, error };
    }

    // 2. Open Target Doc
    let doc, release;
    try {
      const openResult = this.docsService.open(targetDocId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.error('[CrossDocRelation] Failed to open target doc:', e);
      return {
        success: false,
        error: new Error(`Failed to open document: ${targetDocId}`),
      };
    }

    try {
      // 3. Get BlockSuite doc and find target database
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        throw new Error('BlockSuite doc not available');
      }

      // Ensure doc is fully loaded before querying blocks
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === targetDatabaseId
      );

      if (!targetDb) {
        // Database already deleted, consider success
        return { success: true };
      }

      // 4. Delete the property
      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      // Check if property exists
      const properties = dataSource.getAllPropertyIds();
      if (!properties.includes(inverseFieldId)) {
        // Property already deleted, consider success
        return { success: true };
      }

      // Delete the property
      dataSource.propertyDelete(inverseFieldId);

      return { success: true };
    } catch (e) {
      console.error('[CrossDocRelation] Failed to delete inverse relation:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  /**
   * Update the inverseCardinality of an inverse relation in a target document.
   * Called when cardinality is changed on the source column to keep symmetric stealing in sync.
   */
  async updateCrossDocInverseCardinality(
    targetDocId: string,
    targetDatabaseId: string,
    inverseFieldId: string,
    newInverseCardinality: 'one' | 'many'
  ): Promise<CrossDocRelationResult> {
    // 1. Permission Check
    const canModify = await this.canModifyDoc(targetDocId);
    if (!canModify) {
      const error = new PermissionDeniedError(targetDocId);
      console.warn('[CrossDocRelation] Permission denied:', error.message);
      return { success: false, error };
    }

    // 2. Open Target Doc
    let doc, release;
    try {
      const openResult = this.docsService.open(targetDocId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.error('[CrossDocRelation] Failed to open target doc:', e);
      return {
        success: false,
        error: new Error(`Failed to open document: ${targetDocId}`),
      };
    }

    try {
      // 3. Get BlockSuite doc and find target database
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        throw new Error('BlockSuite doc not available');
      }

      // Ensure doc is fully loaded before querying blocks
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === targetDatabaseId
      );

      if (!targetDb) {
        console.warn('[CrossDocRelation] Target database not found');
        return {
          success: false,
          error: new Error('Target database not found'),
        };
      }

      // 4. Update the property data
      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      // Check if property exists
      const properties = dataSource.getAllPropertyIds();
      if (!properties.includes(inverseFieldId)) {
        console.warn('[CrossDocRelation] Inverse property not found');
        return {
          success: false,
          error: new Error('Inverse property not found'),
        };
      }

      // Get current property data and update inverseCardinality
      const currentData = dataSource.propertyDataGet(inverseFieldId);
      if (!currentData) {
        return {
          success: false,
          error: new Error('Could not get property data'),
        };
      }

      dataSource.propertyDataSet(inverseFieldId, {
        ...currentData,
        inverseCardinality: newInverseCardinality,
      });

      console.log('[CrossDocRelation] Updated inverseCardinality:', {
        targetDocId,
        targetDatabaseId,
        inverseFieldId,
        newInverseCardinality,
      });

      return { success: true };
    } catch (e) {
      console.error(
        '[CrossDocRelation] Failed to update inverseCardinality:',
        e
      );
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  /**
   * Update the cardinality of an inverse relation column in a target document.
   * Called when "Backlink Type" is changed on source to sync to inverse.cardinality.
   */
  async updateCrossDocColumnCardinality(
    targetDocId: string,
    targetDatabaseId: string,
    inverseFieldId: string,
    newCardinality: 'one' | 'many'
  ): Promise<CrossDocRelationResult> {
    // Reuse same logic but update cardinality instead of inverseCardinality
    const canModify = await this.canModifyDoc(targetDocId);
    if (!canModify) {
      const error = new PermissionDeniedError(targetDocId);
      console.warn('[CrossDocRelation] Permission denied:', error.message);
      return { success: false, error };
    }

    let doc, release;
    try {
      const openResult = this.docsService.open(targetDocId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.error('[CrossDocRelation] Failed to open target doc:', e);
      return {
        success: false,
        error: new Error(`Failed to open document: ${targetDocId}`),
      };
    }

    try {
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        throw new Error('BlockSuite doc not available');
      }

      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === targetDatabaseId
      );

      if (!targetDb) {
        return {
          success: false,
          error: new Error('Target database not found'),
        };
      }

      const model =
        (targetDb as unknown as { model?: DatabaseBlockModel }).model ??
        targetDb;
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      const properties = dataSource.getAllPropertyIds();
      if (!properties.includes(inverseFieldId)) {
        return {
          success: false,
          error: new Error('Inverse property not found'),
        };
      }

      const currentData = dataSource.propertyDataGet(inverseFieldId);
      if (!currentData) {
        return {
          success: false,
          error: new Error('Could not get property data'),
        };
      }

      dataSource.propertyDataSet(inverseFieldId, {
        ...currentData,
        cardinality: newCardinality,
      });

      console.log('[CrossDocRelation] Updated cardinality:', {
        targetDocId,
        targetDatabaseId,
        inverseFieldId,
        newCardinality,
      });

      return { success: true };
    } catch (e) {
      console.error('[CrossDocRelation] Failed to update cardinality:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  /**
   * Find the title property ID of a database
   */
  private async findTitlePropertyId(
    docId: string,
    databaseId: string
  ): Promise<string> {
    try {
      const { doc, release } = this.docsService.open(docId);
      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) return 'title';

        // Ensure doc is fully loaded before querying blocks
        blockSuiteDoc.load();
        await doc.waitForSyncReady();

        const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
        const targetDb = databases.find(
          (db: { id: string }) => db.id === databaseId
        );

        if (!targetDb) return 'title';

        const model = getDatabaseBlockModel(targetDb);
        const dataSource = createDatabaseBlockDataSource(
          model as DatabaseBlockModel
        );
        const propertyIds = dataSource.getAllPropertyIds();

        for (const id of propertyIds) {
          const propType = dataSource.propertyTypeGet(id);
          const propName = dataSource.propertyNameGet(id);
          if (propType === 'title' || propName?.toLowerCase() === 'title') {
            return id;
          }
        }
      } finally {
        release();
      }
    } catch (e) {
      console.warn('[CrossDocRelation] Failed to find title property:', e);
    }

    return 'title'; // Default fallback
  }

  /**
   * Get rows from a database in another document.
   * Used for Cross-Doc Relation row selection in dropdown.
   */
  async getCrossDocDatabaseRows(
    docId: string,
    databaseId: string,
    displayFieldId: string
  ): Promise<Array<{ id: string; display: string }>> {
    return (
      await this.getCrossDocDatabaseRowsWindow(
        docId,
        databaseId,
        displayFieldId,
        {
          offset: 0,
          limit: 50,
        }
      )
    ).rows;
  }

  async getCrossDocDatabaseRowsWindow(
    docId: string,
    databaseId: string,
    displayFieldId: string,
    request: RelationRowsWindowRequest
  ): Promise<RelationRowsWindow> {
    let doc, release;
    try {
      const openResult = this.docsService.open(docId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.warn('[CrossDocRelation] Failed to open doc for rows:', e);
      return {
        offset: request.offset ?? 0,
        limit: request.limit,
        totalCount: 0,
        hasMore: false,
        rows: [],
      };
    }

    try {
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        console.warn('[CrossDocRelation] No blockSuiteDoc available');
        return {
          offset: request.offset ?? 0,
          limit: request.limit,
          totalCount: 0,
          hasMore: false,
          rows: [],
        };
      }

      // Ensure doc is fully loaded before querying blocks
      // This fixes the issue where databases are not found after page reload
      // or when navigating via ghost nodes
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === databaseId
      );

      if (!targetDb) {
        console.warn('[CrossDocRelation] Target DB not found:', databaseId);
        return {
          offset: request.offset ?? 0,
          limit: request.limit,
          totalCount: 0,
          hasMore: false,
          rows: [],
        };
      }

      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      return getDatabaseRowsWindow(dataSource, displayFieldId, request);
    } catch (e) {
      console.warn('[CrossDocRelation] Failed to get rows:', e);
      return {
        offset: request.offset ?? 0,
        limit: request.limit,
        totalCount: 0,
        hasMore: false,
        rows: [],
      };
    } finally {
      release();
    }
  }

  async getCrossDocRowDisplayValue(
    docId: string,
    databaseId: string,
    displayFieldId: string,
    rowId: string
  ): Promise<string | undefined> {
    let doc, release;
    try {
      const openResult = this.docsService.open(docId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.warn('[CrossDocRelation] Failed to open doc for row display:', e);
      return;
    }

    try {
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        return;
      }

      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === databaseId
      );

      if (!targetDb) {
        return;
      }

      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      return getRowDisplayValueFromDataSource(
        dataSource,
        rowId,
        displayFieldId
      );
    } catch (e) {
      console.warn('[CrossDocRelation] Failed to get row display:', e);
      return;
    } finally {
      release();
    }
  }

  /**
   * Update a cell value in a cross-doc database.
   * Used for bidirectional row link sync.
   *
   * @param docId - Target doc ID
   * @param databaseId - Target database ID
   * @param rowId - Row ID to update
   * @param propertyId - Property/column ID to update
   * @param value - New cell value (string array for relations)
   */
  async updateCrossDocCellValue(
    docId: string,
    databaseId: string,
    rowId: string,
    propertyId: string,
    value: string[]
  ): Promise<CrossDocRelationResult> {
    // 1. Permission Check
    const canModify = await this.canModifyDoc(docId);
    if (!canModify) {
      const error = new PermissionDeniedError(docId);
      console.warn('[CrossDocRelation] Permission denied:', error.message);
      return { success: false, error };
    }

    // 2. Open Target Doc
    let doc, release;
    try {
      const openResult = this.docsService.open(docId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.error('[CrossDocRelation] Failed to open target doc:', e);
      return {
        success: false,
        error: new Error(`Failed to open document: ${docId}`),
      };
    }

    try {
      // 3. Get BlockSuite doc and find target database
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        throw new Error('BlockSuite doc not available');
      }

      // Ensure doc is fully loaded before querying blocks
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === databaseId
      );

      if (!targetDb) {
        throw new DatabaseNotFoundError(databaseId, docId);
      }

      // 4. Update the cell value
      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      // Check if property exists
      const properties = dataSource.getAllPropertyIds();
      if (!properties.includes(propertyId)) {
        console.warn(
          '[CrossDocRelation] Property not found:',
          propertyId,
          'in database:',
          databaseId
        );
        return {
          success: false,
          error: new Error(`Property ${propertyId} not found`),
        };
      }

      // Update the cell value
      dataSource.cellValueChange(rowId, propertyId, value);

      return { success: true };
    } catch (e) {
      console.error('[CrossDocRelation] Failed to update cell value:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  /**
   * Get current cell value from a cross-doc database.
   * Used for bidirectional row link sync to read current inverse values.
   * E018-S008: Extended to return property metadata for Rollup formatting.
   */
  async getCrossDocCellValue(
    docId: string,
    databaseId: string,
    rowId: string,
    propertyId: string
  ): Promise<{
    success: boolean;
    value?: string[];
    propertyType?: string;
    propertyData?: Record<string, unknown>;
    error?: Error;
  }> {
    let doc, release;
    try {
      const openResult = this.docsService.open(docId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.error('[CrossDocRelation] Failed to open target doc:', e);
      return {
        success: false,
        error: new Error(`Failed to open document: ${docId}`),
      };
    }

    try {
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        throw new Error('BlockSuite doc not available');
      }

      // Ensure doc is fully loaded before querying blocks
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const targetDb = databases.find(
        (db: { id: string }) => db.id === databaseId
      );

      if (!targetDb) {
        return { success: true, value: [] }; // No DB = no value
      }

      const model = getDatabaseBlockModel(targetDb);
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      const rawValue = dataSource.cellValueGet(rowId, propertyId);
      const value = this.normalizeToArray(rawValue);

      // E018-S008: Get property metadata for Rollup formatting
      const propertyType = dataSource.propertyTypeGet(propertyId);
      const propertyData = dataSource.propertyDataGet(propertyId) as
        | Record<string, unknown>
        | undefined;

      return { success: true, value, propertyType, propertyData };
    } catch (e) {
      console.error('[CrossDocRelation] Failed to get cell value:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  private normalizeToArray(value: unknown): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [String(value)];
  }
}
